import type { TypographyVariantsOptions } from '@mui/material/styles';

/**
 * Northstar type system.
 *
 * Two faces:
 * - **Inter**: every body, label and figure. The product is data-dense (tables,
 *   dashboards, dialogs); Inter's neutral, compact shapes carry it.
 * - **Space Grotesk**: display only: `h1`–`h3`, i.e. the shell page title and
 *   section headings, plus the auth brand moments. Deliberately NOT on `h4`–`h6`:
 *   those are used as small labels here (15/13/12px), and a display face on 12px
 *   chrome reads as noise rather than brand.
 *
 * The scale is the dense B2B/fintech idiom (14px base body at the 16px root
 * reference), not a marketing scale. Every variant is declared on purpose:
 * anything left undeclared falls back to MUI's defaults, which are built for a
 * 16px-base marketing page and clash here; their `subtitle1` is 16px/400, i.e.
 * *larger* than this `h4`, and their `overline` has a 2.66 line-height.
 *
 * Sizes stay in `rem` with no `clamp()`: `MuiCssBaseline` already scales the root
 * (16px → 14px under 1600px → 13px under 1280px), so the whole scale zooms with
 * the viewport. A fluid `clamp(rem + vw)` on top would scale twice.
 */

/** Fontsource's variable builds register THESE family names, not `Inter` / `Space Grotesk`. */
const INTER = [
  '"Inter Variable"',
  '"Inter"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  'sans-serif',
].join(',');

const GROTESK = ['"Space Grotesk Variable"', '"Space Grotesk"', INTER].join(',');

/** For the brand moments that size their type from the design rather than the scale. */
export const bodyFontFamily = INTER;
export const displayFontFamily = GROTESK;

const typography: TypographyVariantsOptions = {
  fontFamily: INTER,

  // ── Display: Space Grotesk. Real titles only. ──
  h1: {
    fontFamily: GROTESK,
    fontWeight: 700,
    fontSize: '1.75rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
  },
  h2: {
    fontFamily: GROTESK,
    fontWeight: 700,
    fontSize: '1.375rem',
    letterSpacing: '-0.018em',
    lineHeight: 1.3,
  },
  h3: {
    fontFamily: GROTESK,
    fontWeight: 600,
    fontSize: '1.125rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.35,
  },

  // ── Labels: Inter. Section titles, entity names, table group headers. ──
  h4: { fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.4 },
  h5: { fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.5 },
  h6: { fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.5 },

  subtitle1: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.4 },
  subtitle2: { fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.4 },

  // `body2` sits one step above `caption` so the two read as different levels;
  // undeclared, MUI's caption default landed on the same 12px as body2.
  body1: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.6 },
  body2: { fontWeight: 400, fontSize: '0.8125rem', lineHeight: 1.55 },
  caption: { fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.45 },

  button: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    letterSpacing: '0.01em',
    textTransform: 'none',
  },
  overline: {
    fontWeight: 600,
    fontSize: '0.6875rem',
    letterSpacing: '0.08em',
    lineHeight: 1.4,
    textTransform: 'uppercase',
  },
};

export default typography;
